import { lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import MichiganCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import CitySEOHead from "@/components/feature/CitySEOHead";
import { faqs as miFaqs } from "./components/FAQSection";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

const michiganServices = [
  { name: "Emergency Passport Service", description: "Same-day and next-day emergency passport processing for Michigan travelers with urgent international travel needs." },
  { name: "Expedited Passport Renewal", description: "2–3 week expedited passport renewal service for Michigan residents." },
  { name: "New Passport Application", description: "First-time passport application assistance with document prep and acceptance facility scheduling for Michigan residents." },
  { name: "Child Passport Service", description: "Minor passport applications with parental consent documentation for Michigan families." },
  { name: "Lost or Stolen Passport Replacement", description: "Fast replacement of lost, stolen, or damaged passports for Michigan residents." },
  { name: "Name Change Passport Update", description: "Passport name change service after marriage, divorce, or legal name change for Michigan residents." },
];

export default function MichiganPage() {
  return (
    <>
      <CitySEOHead
        city="Michigan"
        state="Michigan"
        stateAbbr="MI"
        route="/states/michigan"
        title="Expedited & Emergency Passport Services Michigan | Same-Day MI Service | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across all Michigan cities. Serving Detroit, Grand Rapids, Ann Arbor, Lansing, and beyond. Regional agency in Detroit. Courier service covers all of Michigan including the Upper Peninsula."
        geoPlacename="Michigan"
        geoPosition="44.3148;-85.6024"
        faqs={miFaqs.map((f) => ({ q: f.question, a: f.answer }))}
        servicesSchema={michiganServices}
        keywords="expedited passport Michigan, emergency passport Michigan, same day passport MI, fast passport Detroit, passport renewal Grand Rapids, passport service Ann Arbor"
      />
      <div className="min-h-screen bg-white">
        <HeroSection />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
            </div>
          }
        >
          <CitiesDirectory />
        </Suspense>
        <MichiganCommunitiesWeServe />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
            </div>
          }
        >
          <StateResources />
        </Suspense>

        <section className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Passport Services
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
                How We Serve Michigan
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                From the auto corridors of Detroit to the lake shores of Traverse City, from the university halls of Ann Arbor to the remote Upper Peninsula — we help Michigan travelers get their passports fast. One regional agency in Detroit serves the entire state, which is why our courier service is so valuable.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-flight-takeoff-line",
                  title: "Emergency Same-Day Service",
                  desc: "For Michigan travelers with flights within 72 hours. We hand-carry your application to the Chicago Passport Agency — no road trip required.",
                },
                {
                  icon: "ri-time-line",
                  title: "Expedited 2–3 Week Service",
                  desc: "Standard expedited processing with overnight shipping and tracking for Michigan residents. Perfect for upcoming summer or winter travel.",
                },
                {
                  icon: "ri-file-list-3-line",
                  title: "New Passport Applications",
                  desc: "First-time applicants in Michigan — we handle document prep, photos, and acceptance facility scheduling. Especially helpful in rural Michigan.",
                },
                {
                  icon: "ri-refresh-line",
                  title: "Passport Renewals",
                  desc: "Michigan renewals by mail or courier. We ensure your application is complete to avoid delays — critical before your winter Caribbean trip.",
                },
                {
                  icon: "ri-flag-line",
                  title: "Canada Border Crossing",
                  desc: "Michigan borders Canada on three sides. We help residents near the Ambassador Bridge, Blue Water Bridge, and Sault Ste. Marie get proper travel documents.",
                },
                {
                  icon: "ri-error-warning-line",
                  title: "Lost, Stolen & Damaged",
                  desc: "Fast replacement for Michigan residents who need a new passport quickly — especially for those traveling across the Canadian border.",
                },
              ].map((svc, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                    <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
            </div>
          }
        >
          <FAQSection />
        </Suspense>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
            </div>
          }
        >
          <FinalCTA />
        </Suspense>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <SEOCrossLinks state="MI" showCities={false} />
          </div>
        </section>
      </div>
    </>
  );
}